/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useLayoutEffect, useRef, useState } from 'react';

// Mantine
import {
	Box,
	Group,
	Image,
	Paper,
	Skeleton,
	Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

// Styles
import classes from './additionality-projects.module.css';

// Components
import { Button, Searchbar } from '@components/atoms';

import {
	CardProject,
	MenuFilter,
} from '@components/molecules';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';
import { IconCirclePlus } from '@tabler/icons-react';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { Pagination } from '@components/atoms';
import { ACLS } from '@config/constants/acls';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { ROLES } from '@config/constants/roles';
import { useAdditionalityProjects } from '@features/catalogs/additionality-projects/list/api/get-additionality-projects';
import { AddAdditionalityProject } from '@features/catalogs/additionality-projects/list/components/AddAdditionalityProject';
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/list/hooks/useAdditionalityProjects';
import { TAdditionalityProject } from '@features/catalogs/additionality-projects/list/types';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACTIVATED_FILTER_OPTIONS } from '@utils/options';
import isNil from 'lodash/isNil';
import { useNavigate } from 'react-router';
import { useCountryCodes } from 'shared/api/get-country-codes';
/* -----------------------------------------------------a--------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AdditionalityProjects = () => {
	{
		/* *************************** HOOKS *************************** */
	}
	const { t } = useTranslation();
	const [isOverflowFilters] = useState(false);
	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const navigate = useNavigate();
	const containerRef = useRef<HTMLDivElement>(null);

	const { data: countryCodes } = useCountryCodes(
		'additionalities'
	);

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const {
		handleChangePaginationSize,
		handleChangePagination,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		handleTechnologyFilter,
		handleCountryFilter,
		handleProjectNameFilter,
		addAdditionalityProjectFormOpened,
		setAddAdditionalityProjectFormOpened,
		handleOpenDrawerAddAdditionalityProject,
		handleActiveFilter,
	} = useAdditionalityProject();

	// APIs

	const {
		data: additionalityProjects,
		isFetching,
		total: totalItems,
		totalPages,
	} = useAdditionalityProjects(
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams
	);

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const { checkAcl } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			<AddAdditionalityProject
				onClose={() => {
					setAddAdditionalityProjectFormOpened(false);
				}}
				opened={addAdditionalityProjectFormOpened}
			/>
			<Paper className={classes.paper}>
				{/* Row filters */}
				<div className={classes.containerFilters}>
					<div className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<div
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterAutocomplete
										}
									>
										<Searchbar
											hiddenFrom="xxl"
											placeholder={t('global.search')}
											size={'sm'}
											onChange={handleProjectNameFilter}
										/>
										<Searchbar
											visibleFrom="xxl"
											placeholder={t('global.search')}
											size={'lg'}
											onChange={handleProjectNameFilter}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleActiveFilter}
												label={t('global.status')}
												size="lg"
												options={ACTIVATED_FILTER_OPTIONS}
												placeholderSearch={t(
													'filters.placeholderSearch.searchStatus'
												)}
												placeholder={t(
													'filters.placeholders.selectStatus'
												)}
												withoutSearch
											/>
										</Box>
										<Box
											className={classes.spacerFilter}
										/>
									</Authorization>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												additionalityProjectsQueryParams[
													'location.countryCode'
												]
													? additionalityProjectsQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											isPersist
											value={
												additionalityProjectsQueryParams.technology
													? additionalityProjectsQueryParams.technology
													: []
											}
											onApply={handleTechnologyFilter}
											size="lg"
											options={PROJECT_TECHNOLOGIES}
											placeholderSearch={t(
												'filters.placeholderSearch.searchTechnology'
											)}
											placeholder={t(
												'filters.placeholders.selectTechnology'
											)}
										/>
									</Box>
								</Box>
							</Group>
						</div>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</div>
					{checkAcl(ACLS.ADDITIONALITIES.CREATE) && (
						<div className={classes.wrapperRowRight}>
							<Authorization
								allowedRoles={[
									ROLES.ADMIN,
									ROLES.SUPERADMIN,
									ROLES.STAFF,
								]}
							>
								<div className={classes.wrapperRowRight}>
									<Button
										leftSection={
											<IconCirclePlus size={23} />
										}
										hiddenFrom="xxl"
										size="md"
										onClick={
											handleOpenDrawerAddAdditionalityProject
										}
									>
										{t('global.add') +
											' ' +
											t('global.project')}
									</Button>
									<Button
										leftSection={
											<IconCirclePlus size={23} />
										}
										visibleFrom="xxl"
										size="lg"
										onClick={
											handleOpenDrawerAddAdditionalityProject
										}
									>
										{t('global.add') +
											' ' +
											t('global.project')}
									</Button>
								</div>
							</Authorization>
						</div>
					)}
				</div>

				<Box className={classes.contentCards}>
					{isFetching ? (
						<Box className={classes.containerSkeletons}>
							{Array.from(Array(5).keys()).map(
								(index) => (
									<Skeleton
										key={index}
										height={260}
										width={260}
										radius="md"
									/>
								)
							)}
						</Box>
					) : Array.isArray(additionalityProjects) &&
					  additionalityProjects.length > 0 ? (
						additionalityProjects.map(
							(project: TAdditionalityProject) => (
								<CardProject
									key={project.id}
									size={'responsive'}
									type={'view-additionality'}
									data={project}
									isAdded={false}
									tco2={2}
									onAdd={() => {}}
									onChangeTco2={() => {}}
									handleShowMoreDetails={() => {
										navigate(
											`/additionality-projects/${project.id}`
										);
									}}
									inactive={!project?.active}
								/>
							)
						)
					) : (
						<Box className={classes.containerEmptyList}>
							<Box
								className={
									classes.containerTextsNoRecords
								}
							>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								<>
									<Text
										className={classes.titleNoRecords}
									>
										{t('global.datatable.noResultsFound')}
									</Text>
									<Text
										className={
											classes.descriptionNoRecords
										}
									>
										{t(
											'global.datatable.noResultsFoundDescription'
										)}
									</Text>
								</>
							</Box>
						</Box>
					)}
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={additionalityProjectsPageSize}
					totalitems={totalItems}
					activepage={additionalityProjectsPage}
					handlechangesize={handleChangePaginationSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};
export default AdditionalityProjects;
